// Suffixes i18next appends to a key for plurals: `t("key", { count })` looks
// up `key_one`, `key_other`… but reports the base `key` when it is missing.
const PLURAL_SUFFIX = /_(zero|one|two|few|many|other|plural|\d+)$/

/**
 * Flattens a translation catalog into the set of its keys, as `t()` receives
 * them (`a.b.c`). Plural variants also register their base key.
 */
export function collectTranslationKeys(catalog, prefix = "") {
	const keys = new Set()

	for (const [name, value] of Object.entries(catalog)) {
		const key = prefix ? `${prefix}.${name}` : name

		if (value !== null && typeof value === "object") {
			for (const nested of collectTranslationKeys(value, key))
				keys.add(nested)
			continue
		}

		keys.add(key)
		keys.add(key.replace(PLURAL_SUFFIX, ""))
	}

	return keys
}

/**
 * Builds the i18next `missingKeyHandler` (OC-140). i18next calls it for a key
 * found in neither the active language nor the fallback one, i.e. a key that
 * renders as its raw name: logging it surfaces a gap in a translation export
 * instead of letting it ship unnoticed.
 *
 * Only keys of the reference catalog (`knownKeys`) are reported. The V3 also
 * passes user data to `t()` (category names, file names…): such a value is
 * never a catalog key, and logging it would leak it to the console and, from
 * there, to Sentry breadcrumbs and replays.
 *
 * Each language/key pair is reported once per session — the same `t()` call
 * re-runs on every render, so an unbounded log would drown the console. A
 * factory, so each test gets a fresh registry.
 */
export function createMissingTranslationKeyLogger(knownKeys) {
	const reported = new Set()

	return (languages, namespace, key) => {
		if (!knownKeys.has(key)) return

		for (const language of languages) {
			const id = `${language}|${key}`
			if (reported.has(id)) continue

			reported.add(id)
			console.error(
				`[i18n] Missing translation key — language: ${language}, key: ${key}`,
			)
		}
	}
}
